// @flow

import map from 'lodash/map';
import get from 'lodash/get';

import moment from 'moment';

import { geocodeByAddress, getLatLng } from '@clark-inc/clark-forms';
import { getFullName, formatTimeWithPeriod, combinedDateAndTime, isFutureDate } from 'clark-utils';

import { typeof postSessionCreate as PostSessionCreateType } from 'actions/sessions';

import { type SessionStatusType } from 'types/sessions';
import { type ClientsType } from 'types/clients';
import { type SubjectsType } from 'types/subjects';
import { type EngagementsType } from 'types/engagements';

type FormTimeObjectType = { time: string, period: string };

const combineDateAndTimeAndPeriod = (date: moment, time: string, period: string): string =>
  combinedDateAndTime(date, moment(formatTimeWithPeriod(time, period), 'h:mm a'));

const getStartAndEndDateTimes = (
  date: moment,
  startTime: FormTimeObjectType,
  endTime: FormTimeObjectType,
): {
  startDateTime: string,
  endDateTime: string,
} => {
  const startDateTime = combineDateAndTimeAndPeriod(date, startTime.time, startTime.period);
  const endDateTime = combineDateAndTimeAndPeriod(date, endTime.time, endTime.period);
  return {
    startDateTime,
    endDateTime,
  };
};

export const getTimeObjFromDateTime = (start: ?string): ?FormTimeObjectType =>
  start
    ? {
        time: moment(start).format('h:mm'),
        period: moment(start).format('a'),
      }
    : null;

type DropdownOptionsType = Array<{ label: string, value: string }>;

export const generateEngagementDropdownOptions = (
  tutoringEngagements: EngagementsType,
  clients: ClientsType,
  subjects: SubjectsType,
): DropdownOptionsType =>
  map(tutoringEngagements, e => {
    const subjectName = get(
      subjects[get(e, 'relationships.subject.data.id')],
      'attributes.name',
      'Unknown Subject',
    );

    const client = clients[get(e, 'relationships.client.data.id')];
    const clientName = client ? getFullName(client.attributes) : 'Unknown Client';

    return {
      value: e.id,
      label: `${clientName}, ${subjectName}`,
    };
  });

export const isScheduling = (sessionDate: ?moment, endTime: ?FormTimeObjectType): boolean =>
  !!sessionDate &&
  (isFutureDate(sessionDate) ||
    (!!endTime &&
      moment(combineDateAndTimeAndPeriod(sessionDate, endTime.time, endTime.period)).isAfter()));

export const calculateRecurrenceCopy = (sessionDate: ?moment, recurrence: ?number) => {
  const toDays = 7 * (Number(recurrence) - 1);
  const lastSessionDate = moment(sessionDate)
    .add(toDays, 'days')
    .format('MMMM Do, YYYY');

  return `The last session will be ${lastSessionDate}.`;
};

export const calculateCTACopy = (
  isCancelation: boolean,
  isRecurring: boolean,
  sessionDate: ?moment,
  endTime: ?FormTimeObjectType,
): string => {
  if (isRecurring) {
    return 'Schedule Upcoming Series';
  }

  if (isScheduling(sessionDate, endTime)) {
    return isCancelation ? 'Schedule Canceled Session' : 'Schedule Session';
  }

  return isCancelation ? 'Log Canceled Session' : 'Log Session';
};

const calculateStatus = (
  isCancelation: boolean,
  sessionDate: ?moment,
  endTime: ?FormTimeObjectType,
): SessionStatusType => {
  if (isCancelation) {
    return 'canceled';
  }
  if (isScheduling(sessionDate, endTime)) {
    return 'pending';
  }

  return 'confirmed';
};

export type FormValuesType = {
  tutoringEngagementId: string,
  startDate: moment,
  startTime: FormTimeObjectType,
  endTime: FormTimeObjectType,
  locationRaw: string,
  isCancelation: boolean,
  isRecurring: boolean,
  sessionRecurrenceCount: number,
};

export const transformSessionData = ({
  startDate,
  startTime,
  endTime,
  tutoringEngagementId,
  locationRaw,
  isCancelation,
  isRecurring,
  sessionRecurrenceCount,
}: FormValuesType): {
  startTime: string,
  endTime: string,
  status: SessionStatusType,
  sessionRecurrenceCount: ?number,
  tutoringEngagementId: string,
} => {
  const { startDateTime, endDateTime } = getStartAndEndDateTimes(startDate, startTime, endTime);

  return {
    startTime: startDateTime,
    endTime: endDateTime,
    locationRaw,
    status: calculateStatus(isCancelation, startDate, endTime),
    tutoringEngagementId,
    sessionRecurrenceCount: isRecurring ? sessionRecurrenceCount : undefined,
  };
};

export const transformAndPost = (dispatchPostSessionCreate: PostSessionCreateType) => async (
  formValues: FormValuesType,
): Promise<void> => {
  const baseSessionData = transformSessionData(formValues);

  const { locationRaw } = formValues;

  let locationLatitude;
  let locationLongitude;
  try {
    const results = await geocodeByAddress(locationRaw);
    const { lat, lng } = await getLatLng(results[0]);
    locationLatitude = lat;
    locationLongitude = lng;
  } finally {
    await dispatchPostSessionCreate({
      ...baseSessionData,
      locationLatitude,
      locationLongitude,
    });
  }
};

export default transformAndPost;



// WEBPACK FOOTER //
// ./src/containers/sessions/create/form/helpers/index.js